import { useState, useRef, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, isEqual, parseISO } from 'date-fns';
import { Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

// Custom DatePicker Component
export function DatePicker({ value, onChange, label, required, dropdownDirection = 'down', disabled = false, compact = false, minDate = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const dropdownRef = useRef(null);

  // Parse date string without timezone conversion (treat as local date)
  const selectedDate = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : null;

  // Parse minDate string without timezone conversion
  const minDateParsed = minDate ? (() => {
    const [year, month, day] = minDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : null;

  // Reset to current month when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentMonth(new Date());
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Get all days in the current month view (including padding days)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Get day of week for first day (0 = Sunday)
  const startDay = monthStart.getDay();

  // Calculate the calendar grid
  const calendarDays = [];

  // Add padding days from previous month
  const prevMonthEnd = new Date(monthStart);
  prevMonthEnd.setDate(0);
  for (let i = startDay - 1; i >= 0; i--) {
    const date = new Date(prevMonthEnd);
    date.setDate(prevMonthEnd.getDate() - i);
    calendarDays.push({ date, isCurrentMonth: false });
  }

  // Add days from current month
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  daysInMonth.forEach(date => {
    calendarDays.push({ date, isCurrentMonth: true });
  });

  // Add padding days from next month to complete the grid
  const remainingDays = 42 - calendarDays.length; // 6 weeks * 7 days
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(monthEnd);
    date.setDate(monthEnd.getDate() + i);
    calendarDays.push({ date, isCurrentMonth: false });
  }

  const handleDateSelect = (date) => {
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block font-semibold text-[var(--text-primary)] text-sm mb-2">
          {label}
          {required && <span style={{ color: '#ef4444' }} className="ml-1">*</span>}
        </label>
      )}

      {/* Display Field */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full rounded-xl font-medium focus:outline-none transition-all duration-200 shadow-sm text-left flex items-center justify-between px-4 py-3`}
        style={{
          background: disabled ? 'var(--bg-muted)' : 'var(--bg-elevated)',
          border: '2px solid var(--border-subtle)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{ color: disabled || !selectedDate ? 'var(--text-muted)' : 'var(--text-primary)' }}>
          {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a date...'}
        </span>
        <Calendar className="w-5 h-5" style={{ color: disabled ? 'var(--text-muted)' : 'var(--primary)' }} />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && !disabled && (
        <div
          className={`absolute z-50 rounded-xl shadow-2xl ${compact ? 'p-1.5 min-w-[230px]' : 'p-1.5 min-w-[250px]'} ${dropdownDirection === 'up' ? 'bottom-full mb-2' : 'mt-2'}`}
          style={{ background: 'var(--bg-elevated)', border: '2px solid var(--border-subtle)' }}
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="p-1 rounded-lg transition-colors"
              style={{ background: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--highlight-10)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: 'var(--text-primary)' }} />
            </button>
            <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            <button
              type="button"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="p-1 rounded-lg transition-colors"
              style={{ background: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--highlight-10)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-primary)' }} />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center text-xs font-semibold py-0.5" style={{ color: 'var(--text-muted)' }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarDays.map((dayInfo, index) => {
              const { date, isCurrentMonth } = dayInfo;
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const isTodayDate = isToday(date);

              // Check if date is disabled (on or before minDate)
              const isDisabled = minDateParsed && (isBefore(date, minDateParsed) || isSameDay(date, minDateParsed));
              const isClickable = isCurrentMonth && !isDisabled;

              // Determine styles
              let buttonStyle = {
                padding: '4px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
                border: 'none',
                cursor: isClickable ? 'pointer' : 'default'
              };

              if (isDisabled) {
                buttonStyle.backgroundColor = 'transparent';
                buttonStyle.color = 'var(--text-faint)';
                buttonStyle.cursor = 'not-allowed';
              } else if (isSelected) {
                buttonStyle.backgroundColor = 'var(--primary)';
                buttonStyle.color = 'white';
              } else if (isTodayDate) {
                buttonStyle.backgroundColor = 'var(--primary-10)';
                buttonStyle.color = 'var(--primary)';
                buttonStyle.fontWeight = '700';
                buttonStyle.border = '1px solid var(--primary-30)';
              } else if (!isCurrentMonth) {
                buttonStyle.backgroundColor = 'transparent';
                buttonStyle.color = 'var(--text-faint)';
              } else {
                buttonStyle.backgroundColor = 'transparent';
                buttonStyle.color = 'var(--text-primary)';
              }

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => isClickable && handleDateSelect(date)}
                  style={buttonStyle}
                  onMouseEnter={(e) => {
                    if (isClickable && !isSelected) {
                      e.currentTarget.style.backgroundColor = 'var(--primary-15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isDisabled) {
                      if (isTodayDate) {
                        e.currentTarget.style.backgroundColor = 'var(--primary-10)';
                      } else {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }
                  }}
                >
                  {format(date, 'd')}
                </button>
              );
            })}
          </div>

          {/* Quick Select Today */}
          {(() => {
            const today = new Date();
            const isTodayDisabled = minDateParsed && (isBefore(today, minDateParsed) || isSameDay(today, minDateParsed));
            return (
              <div className="mt-2 pt-1.5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <button
                  type="button"
                  onClick={() => !isTodayDisabled && handleDateSelect(today)}
                  disabled={isTodayDisabled}
                  className="w-full py-1 text-xs px-2 font-medium rounded transition-colors"
                  style={{
                    background: isTodayDisabled ? 'var(--bg-muted)' : 'var(--primary-10)',
                    color: isTodayDisabled ? 'var(--text-faint)' : 'var(--text-primary)',
                    cursor: isTodayDisabled ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (!isTodayDisabled) e.currentTarget.style.background = 'var(--primary-15)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isTodayDisabled) e.currentTarget.style.background = 'var(--primary-10)';
                  }}
                >
                  Today
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Custom TimePicker Component
export function TimePicker({ value, onChange, label, required }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState(value ? value.split(':')[0] : '09');
  const [minutes, setMinutes] = useState(value ? value.split(':')[1] : '00');
  const dropdownRef = useRef(null);
  const hoursScrollRef = useRef(null);
  const minutesScrollRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update parent when hours or minutes change
  useEffect(() => {
    if (hours && minutes) {
      onChange(`${hours}:${minutes}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, minutes]);

  // Auto-scroll to selected time when dropdown opens
  useEffect(() => {
    if (isOpen && hoursScrollRef.current && minutesScrollRef.current) {
      // Scroll to selected hour (adjusted for 14px base font size: 24px * 0.875 = 21px)
      const hourIndex = parseInt(hours);
      const minuteIndex = parseInt(minutes);

      // Scroll so selected item is at the top
      hoursScrollRef.current.scrollTop = hourIndex * 21;
      minutesScrollRef.current.scrollTop = minuteIndex * 21;
    }
  }, [isOpen, hours, minutes]);

  // Update hours and minutes when value prop changes
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h);
      setMinutes(m);
    }
  }, [value]);

  // Format time for display (12-hour format)
  const formatDisplayTime = () => {
    if (!value) return 'Select a time...';

    const [h, m] = value.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${m} ${ampm}`;
  };

  // Generate hour and minute options
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
        {label}
        {required && <span style={{ color: '#ef4444' }} className="ml-1">*</span>}
      </label>

      {/* Display Field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 rounded-xl font-medium focus:outline-none transition-all duration-200 shadow-sm text-left flex items-center justify-between"
        style={{
          background: 'var(--bg-elevated)',
          border: '2px solid var(--border-subtle)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ color: !value ? 'var(--text-muted)' : 'var(--text-primary)' }}>
          {formatDisplayTime()}
        </span>
        <Clock className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
      </button>

      {/* Time Picker Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-2 rounded-xl shadow-2xl p-1.5 w-full"
          style={{ background: 'var(--bg-elevated)', border: '2px solid var(--border-subtle)' }}
        >
          <div className="text-center text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Select Time</div>

          <div className="flex gap-1.5">
            {/* Hours */}
            <div className="flex-1">
              <div className="text-xs font-semibold mb-0.5 text-center" style={{ color: 'var(--text-secondary)' }}>Hour</div>
              <div ref={hoursScrollRef} className="h-24 overflow-y-auto rounded" style={{ border: '1px solid var(--border-subtle)' }}>
                {hourOptions.map(hour => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => setHours(hour)}
                    className="w-full px-1 py-0.5 text-xs font-medium transition-colors text-center"
                    style={{
                      background: hours === hour ? 'var(--primary)' : 'transparent',
                      color: hours === hour ? 'white' : 'var(--text-primary)',
                    }}
                    onMouseEnter={(e) => {
                      if (hours !== hour) e.currentTarget.style.background = 'var(--primary-10)';
                    }}
                    onMouseLeave={(e) => {
                      if (hours !== hour) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {parseInt(hour) === 0 ? '12 AM' :
                     parseInt(hour) < 12 ? `${parseInt(hour)} AM` :
                     parseInt(hour) === 12 ? '12 PM' :
                     `${parseInt(hour) - 12} PM`}
                  </button>
                ))}
              </div>
            </div>

            {/* Minutes */}
            <div className="flex-1">
              <div className="text-xs font-semibold mb-0.5 text-center" style={{ color: 'var(--text-secondary)' }}>Minute</div>
              <div ref={minutesScrollRef} className="h-24 overflow-y-auto rounded" style={{ border: '1px solid var(--border-subtle)' }}>
                {minuteOptions.map(minute => (
                  <button
                    key={minute}
                    type="button"
                    onClick={() => setMinutes(minute)}
                    className="w-full px-1 py-0.5 text-xs font-medium transition-colors text-center"
                    style={{
                      background: minutes === minute ? 'var(--primary)' : 'transparent',
                      color: minutes === minute ? 'white' : 'var(--text-primary)',
                    }}
                    onMouseEnter={(e) => {
                      if (minutes !== minute) e.currentTarget.style.background = 'var(--primary-10)';
                    }}
                    onMouseLeave={(e) => {
                      if (minutes !== minute) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {minute}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Confirm Button */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full mt-1 py-0.5 px-2 font-semibold rounded transition-colors text-xs"
            style={{
              background: 'var(--primary)',
              color: 'white',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--primary)'}
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}
